import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { insightConfigApi } from '../api/InsightConfigApi'
import {
  SaveInsightConfigFlow,
  type SaveInsightConfigMode,
  type SaveInsightConfigResult,
} from '../flows/SaveInsightConfigFlow'
import type {
  ConfigValidationErrors,
  InsightConfig,
  InsightConfigJsonField,
  InsightConfigMeta,
} from '../models/insightConfig.types'
import type { WorkbenchAsyncState } from '../models/workbench.types'
import {
  INSIGHT_CONFIG_JSON_FIELDS,
  buildJsonEditors,
  createEmptyInsightConfig,
  normalizeInsightConfig,
  parseJsonField,
} from '../service/insightConfig'
import {
  InsightConfigServiceError,
  buildPipelinePayload,
  buildPipelineStepEditors,
  hasLlmPipelineStep,
  type PipelineStepEditor,
} from '../service/insightConfigPipeline'
import { formatStructuredInsightValue, isInsightRecord } from '../service/insightValue'

import { useInsightsRbac } from './useInsightsRbac'

import { ApiError } from '@/shared/http'

interface InsightConfigEditorApiPort {
  meta: (signal?: AbortSignal) => Promise<InsightConfigMeta>
  get: (id: number, signal?: AbortSignal) => Promise<InsightConfig>
}

export interface UseInsightConfigEditorOptions {
  configId: MaybeRefOrGetter<number | undefined>
  canAuthor?: MaybeRefOrGetter<boolean>
  api?: InsightConfigEditorApiPort
  flow?: SaveInsightConfigFlow
}

export interface ConfigPayloadPreview {
  text: string
  errorKey: string
}

export interface UseInsightConfigEditor {
  form: Ref<InsightConfig>
  meta: ComputedRef<InsightConfigMeta | null>
  jsonEditors: Ref<Record<InsightConfigJsonField, string>>
  pipelineEditors: Ref<PipelineStepEditor[]>
  fieldErrors: ComputedRef<Record<string, string[]>>
  nonFieldErrors: ComputedRef<readonly string[]>
  state: ComputedRef<WorkbenchAsyncState>
  error: ComputedRef<ApiError | null>
  isSubmitting: ComputedRef<boolean>
  isCreate: ComputedRef<boolean>
  noticeKey: ComputedRef<string>
  deleteBlockedCount: ComputedRef<number>
  llmPipelineWarning: ComputedRef<boolean>
  payloadPreview: ComputedRef<ConfigPayloadPreview>
  reload: () => Promise<void>
  save: (mode?: SaveInsightConfigMode) => Promise<SaveInsightConfigResult | null>
  deleteConfig: () => Promise<'deleted' | 'blocked' | null>
  resetJsonField: (field: InsightConfigJsonField) => void
}

const EMPTY_META: InsightConfigMeta = {
  status_choices: [],
  outcome_category_choices: [],
  severity_choices: [],
  confidence_choices: [],
  content_type_choices: [],
  default_pipeline_steps: [],
  pipeline_step_types: [],
  supported_process_transforms: [],
  llm_supported_models: [],
  llm_scope_options: [],
}

function validationErrors(): ConfigValidationErrors {
  return { fields: {}, nonField: [] }
}

function stringRequired(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

export function useInsightConfigEditor(
  options: UseInsightConfigEditorOptions,
): UseInsightConfigEditor {
  const rbac = options.canAuthor === undefined ? useInsightsRbac() : null
  const canAuthor = computed(() =>
    options.canAuthor === undefined
      ? (rbac?.canAuthorInsights.value ?? false)
      : toValue(options.canAuthor),
  )
  const api = options.api ?? insightConfigApi
  const flow = options.flow ?? new SaveInsightConfigFlow()
  const meta = ref<InsightConfigMeta | null>(null)
  const form = ref<InsightConfig>(createEmptyInsightConfig())
  const original = ref<Partial<InsightConfig> | null>(null)
  const jsonEditors = ref(buildJsonEditors(form.value))
  const pipelineEditors = ref<PipelineStepEditor[]>([])
  const errors = ref<ConfigValidationErrors>(validationErrors())
  const state = ref<WorkbenchAsyncState>('idle')
  const error = ref<ApiError | null>(null)
  const isSubmitting = ref(false)
  const noticeKey = ref('')
  const deleteBlockedCount = ref(0)
  let generation = 0
  let controller: AbortController | null = null
  let mutationController: AbortController | null = null

  const configId = computed(() => toValue(options.configId))
  const isCreate = computed(() => configId.value === undefined)

  function resetRequest(): void {
    generation += 1
    controller?.abort()
    controller = null
    mutationController?.abort()
    mutationController = null
  }

  function hydrate(config: InsightConfig, source: Partial<InsightConfig> | null): void {
    form.value = normalizeInsightConfig(config, meta.value ?? EMPTY_META)
    original.value = source
    jsonEditors.value = buildJsonEditors(form.value)
    pipelineEditors.value = buildPipelineStepEditors(
      form.value.pipeline_steps,
      meta.value?.default_pipeline_steps,
    )
    errors.value = validationErrors()
  }

  async function reload(): Promise<void> {
    resetRequest()
    if (!canAuthor.value) {
      state.value = 'idle'
      return
    }
    const request = generation
    const activeController = new AbortController()
    controller = activeController
    state.value = 'loading'
    error.value = null
    noticeKey.value = ''
    deleteBlockedCount.value = 0
    try {
      const id = configId.value
      const [nextMeta, loaded] = await Promise.all([
        api.meta(activeController.signal),
        id === undefined ? Promise.resolve(null) : api.get(id, activeController.signal),
      ])
      if (request !== generation || activeController.signal.aborted || id !== configId.value) return
      meta.value = nextMeta
      hydrate(loaded ?? createEmptyInsightConfig(nextMeta), loaded)
      state.value = 'ready'
    } catch (cause) {
      if (request !== generation || activeController.signal.aborted) return
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled') return
      error.value = normalized
      state.value = 'error'
    }
  }

  function composePayload(): { config: InsightConfig | null; errors: ConfigValidationErrors } {
    const nextErrors = validationErrors()
    const next = { ...form.value }
    for (const field of [
      'enum',
      'label',
      'status',
      'outcome_category',
      'severity',
      'confidence',
      'content_type',
    ] as const) {
      if (!stringRequired(next[field]))
        nextErrors.fields[field] = ['insights.sysadmin.config.validation.required']
    }
    const seconds = Number(next.default_refresh_interval_seconds)
    if (!Number.isInteger(seconds) || seconds < 1) {
      nextErrors.fields['default_refresh_interval_seconds'] = [
        'insights.sysadmin.config.validation.seconds',
      ]
    } else next.default_refresh_interval_seconds = seconds

    for (const field of INSIGHT_CONFIG_JSON_FIELDS) {
      try {
        Object.assign(next, { [field]: parseJsonField(field, jsonEditors.value[field]) })
      } catch (cause) {
        const key =
          cause instanceof InsightConfigServiceError
            ? cause.code
            : 'insights.sysadmin.config.jsonInvalid'
        nextErrors.fields[field] = [key]
      }
    }
    try {
      next.pipeline_steps = buildPipelinePayload(pipelineEditors.value)
    } catch (cause) {
      const field =
        cause instanceof InsightConfigServiceError
          ? (cause.field ?? 'pipeline_steps')
          : 'pipeline_steps'
      const key =
        cause instanceof InsightConfigServiceError
          ? cause.code
          : 'insights.sysadmin.config.pipeline.invalid'
      nextErrors.fields[field] = [key]
    }
    return {
      config: Object.keys(nextErrors.fields).length === 0 ? next : null,
      errors: nextErrors,
    }
  }

  async function save(mode?: SaveInsightConfigMode): Promise<SaveInsightConfigResult | null> {
    const prepared = composePayload()
    errors.value = prepared.errors
    noticeKey.value = ''
    deleteBlockedCount.value = 0
    if (!canAuthor.value || !prepared.config) return null
    isSubmitting.value = true
    mutationController?.abort()
    const activeController = new AbortController()
    mutationController = activeController
    try {
      const result = await flow.save(
        prepared.config,
        mode ?? (isCreate.value ? 'create' : 'patch'),
        {
          original: original.value,
          ...(meta.value ? { meta: meta.value } : {}),
          signal: activeController.signal,
        },
      )
      if (result.kind === 'validation') errors.value = result.errors
      if (result.kind === 'no_changes') noticeKey.value = 'insights.sysadmin.config.noChanges'
      if (result.kind === 'saved') {
        hydrate(result.config, result.config)
        noticeKey.value = 'insights.sysadmin.config.saved'
      }
      return result
    } catch (cause) {
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind !== 'canceled') error.value = normalized
      return null
    } finally {
      if (mutationController === activeController) mutationController = null
      isSubmitting.value = false
    }
  }

  async function deleteConfig(): Promise<'deleted' | 'blocked' | null> {
    if (!canAuthor.value || form.value.id === undefined) return null
    isSubmitting.value = true
    deleteBlockedCount.value = 0
    mutationController?.abort()
    const activeController = new AbortController()
    mutationController = activeController
    try {
      const result = await flow.deleteConfig(form.value.id, activeController.signal)
      if (result.kind === 'blocked') {
        deleteBlockedCount.value = result.insightsCount
        return 'blocked'
      }
      noticeKey.value = 'insights.sysadmin.config.deleted'
      return 'deleted'
    } catch (cause) {
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind !== 'canceled') error.value = normalized
      return null
    } finally {
      if (mutationController === activeController) mutationController = null
      isSubmitting.value = false
    }
  }

  function resetJsonField(field: InsightConfigJsonField): void {
    jsonEditors.value = {
      ...jsonEditors.value,
      [field]: JSON.stringify(
        meta.value ? createEmptyInsightConfig(meta.value)[field] : null,
        null,
        2,
      ),
    }
  }

  const llmPipelineWarning = computed(() => {
    try {
      const value = parseJsonField('llm_config', jsonEditors.value.llm_config)
      return (
        Boolean(isInsightRecord(value) && value['enabled']) &&
        !hasLlmPipelineStep(pipelineEditors.value)
      )
    } catch {
      return false
    }
  })
  const payloadPreview = computed<ConfigPayloadPreview>(() => {
    const prepared = composePayload()
    return prepared.config
      ? { text: formatStructuredInsightValue(prepared.config), errorKey: '' }
      : { text: '', errorKey: 'insights.sysadmin.config.previewInvalid' }
  })

  watch([configId, canAuthor], () => void reload(), { immediate: true })
  onScopeDispose(resetRequest)

  return {
    form,
    meta: computed(() => meta.value),
    jsonEditors,
    pipelineEditors,
    fieldErrors: computed(() => errors.value.fields),
    nonFieldErrors: computed(() => errors.value.nonField),
    state: computed(() => state.value),
    error: computed(() => error.value),
    isSubmitting: computed(() => isSubmitting.value),
    isCreate,
    noticeKey: computed(() => noticeKey.value),
    deleteBlockedCount: computed(() => deleteBlockedCount.value),
    llmPipelineWarning,
    payloadPreview,
    reload,
    save,
    deleteConfig,
    resetJsonField,
  }
}
